/**
 * Índice Digital del expediente electrónico (D.S. 029-2021-PCM arts. 36-48).
 *
 * Espejo de `MineTrace.Models.Response.Expedientes.*`. Los enums viajan como `int`
 * y llegan además con su nombre (`*Name`/`*Nombre`); la UI ramifica SIEMPRE por el
 * número — el nombre es para depurar y para no depender de un `switch` exhaustivo.
 */

// ── Modo de incorporación (art. 48) ──────────────────────────────────────────
/** El sistema generó el documento. No procede de ninguna digitalización. */
export const INDICE_MODO_NATIVO_DIGITAL = 0
/** Archivo cargado por un usuario: copia digitalizada SIN fedatario juramentado (D.L. 681). */
export const INDICE_MODO_DIGITALIZADO_SIN_FEDATARIO = 1

// ── Veredicto sobre el sello (IndiceSelloVeredicto) ──────────────────────────
export const INDICE_VEREDICTO_INTACTO = 0
/** La única alarma. Ver `esAlarma`. */
export const INDICE_VEREDICTO_ALTERADO = 1
export const INDICE_VEREDICTO_NO_VERIFICABLE = 2
export const INDICE_VEREDICTO_NO_DISPONIBLE = 3

// ── Estado para pintar (IndiceVerificacionEstado) ────────────────────────────
export const INDICE_ESTADO_INTEGRO = 0
/**
 * Sello íntegro + expediente CRECIDO desde entonces. Es el estado NORMAL de un
 * expediente append-only vivo: severidad `Ninguna`. NUNCA se pinta como aviso.
 */
export const INDICE_ESTADO_MODIFICADO_DESDE_EL_SELLO = 1
export const INDICE_ESTADO_ALTERADO = 2
export const INDICE_ESTADO_NO_VERIFICABLE = 3
export const INDICE_ESTADO_NO_DISPONIBLE = 4

// ── Severidad (IndiceVerificacionSeveridad) ──────────────────────────────────
/**
 * LO ÚNICO QUE DECIDE EL COLOR. Cubre `Integro` Y `ModificadoDesdeElSello`: un
 * expediente que ha crecido está exactamente igual de sano que uno que no.
 */
export const INDICE_SEVERIDAD_NINGUNA = 0
/** No se ha podido comprobar. Tono neutro, jamás "no coincide". */
export const INDICE_SEVERIDAD_SIN_VEREDICTO = 1
/** El único caso en que la interfaz puede decir que algo no cuadra con lo sellado. */
export const INDICE_SEVERIDAD_ALARMA = 2

// ── Reserialización (informativa, no decide nada) ────────────────────────────
export const INDICE_RESERIALIZACION_NO_APLICA = 0
export const INDICE_RESERIALIZACION_COINCIDE = 1
export const INDICE_RESERIALIZACION_DIVERGE = 2
export const INDICE_RESERIALIZACION_NO_SE_PUDO = 3

// ── Hito que provocó el sellado (IndiceVersionTrigger) ───────────────────────
export const INDICE_TRIGGER_INITIAL_APPROVAL = 0
export const INDICE_TRIGGER_MODIFICATION_REQUEST = 1
export const INDICE_TRIGGER_MINER_VERIFICATION = 2
export const INDICE_TRIGGER_ON_DEMAND = 3
export const INDICE_TRIGGER_BASELINE = 4

/** Serie centinela: el ítem no tiene serie documental asignada en el catálogo. */
export const SERIE_SIN_CLASIFICAR = 'MT-99'

// ── Índice en vivo ───────────────────────────────────────────────────────────

/** Metadatos del expediente (art. 44). */
export interface IndiceMetadatosResponse {
  codigoUnico: string
  /** ExpedienteType: 0 Miner, 1 Batch. */
  tipo: number
  tipoNombre: string
  entidadProductora: string | null
  entidadProductoraRuc: string | null
  titularRazonSocial: string | null
  titularRuc: string | null
  cuidCode: string | null
  fechaApertura: string | null
  fechaUltimaActualizacion: string | null
  fechaCierre: string | null
  /** ExpedienteStatus: 0 Draft, 1 Active, 2 Revoked. */
  estado: number
  estadoNombre: string
  totalDocumentos: number
  totalFolios: number
  chainHead: string | null
  jurisdiccion: string
  idioma: string
}

/** Una entrada foliada del índice (art. 42). El orden punteado define el foliado. */
export interface IndiceEntradaResponse {
  itemId: string
  /** Correlativo punteado ("2.3.1"). ES EL ORDEN PRIMARIO: nunca reagrupar por serie. */
  orden: string
  parentItemId: string | null
  titulo: string
  descripcion: string | null
  itemType: number
  itemTypeNombre: string
  serieCodigo: string
  serieNombre: string
  /** Criterio interno del equipo, NO un plazo legal. */
  serieRetencionAnios: number | null
  fechaProduccion: string
  /** Resumen criptográfico del contenido del documento. */
  resumenHash: string
  folioDesde: number
  folioHasta: number
  folios: number
  /** Ver `INDICE_MODO_*` (art. 48). */
  modo: number
  modoNombre: string
  formato: string | null
  tamanoBytes: number | null
  nombreArchivo: string | null
  tieneArchivo: boolean
  /** Posición 1..N del documento dentro del expediente, en el orden del foliado. */
  correlativo: number
  /** Cuándo entró al expediente. Distinta de `fechaProduccion`, que es cuándo se emitió. */
  fechaIncorporacion: string | null
  /** Páginas del documento. Hoy coincide con `folios`; las reglas para que diverjan están pendientes. */
  paginas: number
  extension: string | null
}

/** Expediente de lote apuntado desde éste. No folia: es un expediente autónomo. */
export interface IndiceRelacionadoResponse {
  itemId: string
  codigoDestino: string | null
  titulo: string
  fechaProduccion: string
}

/** Agregado por serie documental. */
export interface IndiceSerieResumenResponse {
  codigo: string
  nombre: string
  /** Criterio interno del equipo, NO un plazo legal. */
  retencionAnios: number | null
  documentos: number
  folios: number
}

/**
 * Índice digital EN VIVO. NO es una versión sellada: se calcula en cada petición y
 * cambia en cuanto entra un documento. Lo oponible son las versiones selladas.
 */
export interface ExpedienteIndiceResponse {
  metadatos: IndiceMetadatosResponse
  /** Documentos foliados, YA en orden punteado. */
  entradas: IndiceEntradaResponse[]
  relacionados: IndiceRelacionadoResponse[]
  resumenPorSerie: IndiceSerieResumenResponse[]
  /** Sólo en expedientes de lote: el expediente del minero del que nace. Navegación, no se firma. */
  expedienteOrigen?: IndiceExpedienteOrigenResponse | null
  /** Fecha de creación del expediente. Cabecera, no se firma. */
  fechaCreacion?: string | null
  /** Sólo en lotes: derecho minero de origen ("CÓDIGO · NOMBRE"). */
  derechoMinero?: string | null
  /** Sólo en lotes: ubigeo (6 dígitos) del derecho minero de origen. */
  ubigeo?: string | null
}

/** Expediente del minero del que nace un expediente de lote. */
export interface IndiceExpedienteOrigenResponse {
  codigo: string
  titular: string | null
  ruc: string | null
}

// ── Versiones selladas ───────────────────────────────────────────────────────

/** Una versión SELLADA del índice digital (arts. 42-43). */
export interface IndiceVersionResponse {
  versionNumber: number
  /** Código de Verificación Digital impreso (art. 36), `XXXX-XXXX-XXXX`. No es un secreto. */
  cvd: string
  /** Instante del sellado SEGÚN NUESTRO SERVIDOR (no hay TSA acreditada). */
  generatedAt: string
  /** Ver `INDICE_TRIGGER_*`. */
  triggerType: number
  triggerTypeName: string
  triggerSourceEntityId: string | null
  /** UUID vacío cuando lo disparó el sistema sin usuario detrás. */
  generatedByUserId: string
  generatedByUserName: string | null
  itemCount: number
  folioFrom: number
  folioTo: number
  expedienteChainHead: string | null
  /** SHA-256 (hex) de los bytes canónicos del XML sellado. Público a propósito. */
  indexDigest: string
  signatureAlgorithm: string
  /** Referencia corta y NO reversible a la clave de firma (16 hex). */
  signingKeyRef: string
  /**
   * Firmado con la clave EFÍMERA de proceso: criptográficamente correcto pero SIN
   * valor legal y no verificable tras reiniciar el servidor.
   */
  signingKeyIsEphemeral: boolean
  /** Sello de tiempo RFC 3161 de una TSA acreditada. HOY SIEMPRE `false`. */
  hasTimeStamp: boolean
  /** `false` = la subida del XML falló en su día. NO es indicio de manipulación. */
  hasXml: boolean
}

// ── Verificación de una versión sellada ──────────────────────────────────────

/** Novedades desde el sello. Recuentos, no un diff. No nulo ⇒ el sello está INTACTO. */
export interface IndiceCambiosResponse {
  documentosNuevos: number
  documentosSellados: number
  documentosAhora: number
  foliosSellados: number
  foliosAhora: number
  foliosNuevos: number
  ultimaIncorporacion: string | null
}

/**
 * Veredicto sobre UNA versión sellada.
 *
 * DOS EJES QUE LA PANTALLA NO PUEDE MEZCLAR: `veredicto` dice si el sello se
 * sostiene; `cambios` dice qué ha entrado desde entonces. El expediente es
 * APPEND-ONLY: que haya crecido es lo normal y NO es una advertencia. El color se
 * elige SIEMPRE por `severidad` —nunca por `estado !== INDICE_ESTADO_INTEGRO`— y
 * sólo `esAlarma` autoriza a decir que algo no cuadra. No existe un `esValido` a
 * propósito.
 */
export interface IndiceVerificacionResponse {
  versionNumber: number
  cvd: string
  generatedAt: string
  /** Ver `INDICE_VEREDICTO_*`. */
  veredicto: number
  veredictoName: string
  /** Ver `INDICE_ESTADO_*`. */
  estado: number
  estadoName: string
  /** Ver `INDICE_SEVERIDAD_*`. LO ÚNICO que debe decidir el color. */
  severidad: number
  severidadName: string
  /** De qué responde el veredicto. Hoy sólo el sello. */
  alcance: number
  alcanceName: string
  /** Comprobación SECUNDARIA e informativa. Ver `INDICE_RESERIALIZACION_*`. */
  reserializacion: number
  reserializacionName: string
  /** Sólo con veredicto Alterado. Ni una caída de KMS ni un expediente que ha crecido. */
  esAlarma: boolean
  /** La criptografía confirma el sello. `false` NO significa "manipulado". */
  selloVerificado: boolean
  /** Explicación en lenguaje de usuario, o nulo cuando no hay nada que explicar. */
  detalle: string | null
  /** Novedades desde el sello, o nulo si el expediente sigue siendo el que se selló. */
  cambios: IndiceCambiosResponse | null
}

// ── Verificación PÚBLICA por CVD (D.S. 029-2021-PCM arts. 36-37 y 45) ────────
//
// Espejo de `MineTrace.Models.Response.Expedientes.VerificacionPublicaResponse`.
// La lista de campos de allí es CERRADA y está protegida por un test de lista
// blanca sobre el JSON: este espejo NO debe crecer por su cuenta, y la pantalla
// pública no debe enseñar nada que no esté aquí.

/** La firma acredita el documento. */
export const VERIFICACION_PUBLICA_AUTENTICO = 0
/**
 * LA ÚNICA RESPUESTA NEGATIVA de las cuatro, y la única que la pantalla puede
 * pintar como tal. Sale sólo del veredicto `Alterado`: ninguna incidencia de
 * infraestructura llega hasta aquí.
 */
export const VERIFICACION_PUBLICA_NO_COINCIDE = 1
/** Nadie puede pronunciarse y volver mañana no lo cambia. NO ES UNA ACUSACIÓN. */
export const VERIFICACION_PUBLICA_NO_COMPROBABLE = 2
/** Incidencia PASAJERA del sistema. "Vuelva a intentarlo", jamás "es falso". */
export const VERIFICACION_PUBLICA_NO_DISPONIBLE = 3

/**
 * Lo que lee una persona cualquiera tras teclear el CVD impreso.
 *
 * NO HAY —NI PUEDE HABER— UN `esValido` AQUÍ. Con un booleano, "Cloud KMS está
 * caído" y "el documento no es el que se firmó" acaban en el mismo `false`, y ese
 * `false` lo lee alguien que no sabe qué es KMS: diez minutos de caída se
 * convertirían en una acusación de falsificación contra una empresa minera,
 * publicada en una página sin autenticar. El color, el icono y el título los
 * decide SIEMPRE `estado`, uno por uno. Ver `verificacionPublica.ts`.
 */
export interface VerificacionPublicaResponse {
  /** Código del expediente, el mismo que consta impreso en el índice. */
  expedienteCode: string
  titularRazonSocial: string | null
  /** RUC del titular. Sale porque es público en SUNAT y permite contrastar el papel. */
  titularRuc: string | null
  versionNumber: number
  /** Instante del sellado SEGÚN NUESTRO SERVIDOR. No hay TSA acreditada detrás. */
  generatedAt: string
  folioFrom: number
  folioTo: number
  /** Documentos que constaban en la versión sellada. No los de hoy. */
  itemCount: number
  /** Ver `VERIFICACION_PUBLICA_*`. */
  estado: number
  estadoName: string
  /**
   * Frase de una LISTA CERRADA de cuatro, redactada por el backend. SE ENSEÑA TAL
   * CUAL: no se reescribe, no se resume y no se concatena con nada que le cambie
   * el sentido.
   */
  mensaje: string
}
